<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Calendar from '@/corePagesModule/components/Calendar.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { i18n } from '@/i18n';

const t = i18n.t;

const props = defineProps<{
  modelValue: string | number | null;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const rootRef = ref<HTMLElement | null>(null);
const calendarRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const selectedDate = ref<Date | null>(parseDateValue(props.modelValue));
const calendarStyle = ref<Record<string, string>>({});

const displayValue = computed(() => formatDateValue(selectedDate.value));

watch(
  () => props.modelValue,
  (value) => {
    selectedDate.value = parseDateValue(value);
  }
);

/**
 * 値をローカル日付に変換する。
 * @param value - 変換対象
 */
function parseDateValue(value: string | number | null): Date | null {
  if (value == null || value === '') return null;

  const match = String(value)
    .trim()
    .match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  if (date.getFullYear() !== Number(year) || date.getMonth() !== Number(month) - 1 || date.getDate() !== Number(day)) return null;
  return date;
}

/**
 * 日付を保存用のYYYY-MM-DDに変換する。
 * @param date - 変換対象
 */
function formatDateValue(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * カレンダーの開閉を切り替える。
 */
const toggleCalendar = (): void => {
  isOpen.value = !isOpen.value;
};

/**
 * トリガー位置に合わせてカレンダーをviewport基準で配置する。
 */
const updateCalendarPosition = (): void => {
  const root = rootRef.value;
  if (!root) return;

  const rect = root.getBoundingClientRect();
  const width = Math.min(420, window.innerWidth - 16);
  const left = Math.min(Math.max(rect.left, 8), window.innerWidth - width - 8);
  const calendarHeight = calendarRef.value?.offsetHeight ?? 360;
  const bottomTop = rect.bottom + 4;
  const top = bottomTop + calendarHeight <= window.innerHeight - 8 ? bottomTop : Math.max(8, rect.top - calendarHeight - 4);

  calendarStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
};

/**
 * カレンダーの選択値を保存値へ反映する。
 * @param value - 選択日
 */
const updateSelectedDate = (value: Date | null): void => {
  selectedDate.value = value;
  emits('update:modelValue', formatDateValue(value));
  isOpen.value = false;
};

/**
 * コンポーネント外クリックでカレンダーを閉じる。
 * @param event - ポインターイベント
 */
const handlePointerDown = (event: PointerEvent): void => {
  const target = event.target as Node;
  if (rootRef.value?.contains(target) || calendarRef.value?.contains(target)) return;
  isOpen.value = false;
};

watch(isOpen, async (value) => {
  if (!value) return;
  await nextTick();
  updateCalendarPosition();
});

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown);
  window.addEventListener('resize', updateCalendarPosition);
  window.addEventListener('scroll', updateCalendarPosition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handlePointerDown);
  window.removeEventListener('resize', updateCalendarPosition);
  window.removeEventListener('scroll', updateCalendarPosition, true);
});
</script>

<template>
  <div ref="rootRef" class="condition-date-input">
    <button class="condition-date-input__trigger" type="button" @click="toggleCalendar">
      <span :class="{ 'condition-date-input__placeholder': !displayValue }">{{ displayValue || t('日付を選択') }}</span>
      <Mdi icon="calendar_month" />
    </button>
    <Teleport to="body">
      <div v-if="isOpen" ref="calendarRef" class="condition-date-input__calendar" :style="calendarStyle">
        <Calendar :model-value="selectedDate" hide-time @update:model-value="updateSelectedDate" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.condition-date-input {
  position: relative;
  width: 100%;

  &__trigger {
    width: 100%;
    min-height: 32px;
    padding: 4px 8px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    background-color: $semanticWhite;
    color: $textDefault;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    column-gap: 6px;
    text-align: left;
    cursor: pointer;
  }

  &__placeholder {
    color: $textSecondary;
  }

  &__calendar {
    position: fixed;
    z-index: 1050;
  }
}
</style>
